import type {
    FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ISidebarMenuSubButtonProps from "./type";
import stylesheet, {
    SIDEBAR_SUB_BUTTON_VARIANT,
    getSidebarSubButtonColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSidebarSubItemContext,
    useSidebarRootContext,
    useSidebarContext
} from "../../context";
import type {
    SidebarWebPropsType
} from "../../type";
import {
    getSidebarActiveWeight,
    getSidebarRowRadius,
    getSidebarMeasures
} from "../../stylesheet";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const SidebarMenuSubButton: FC<ISidebarMenuSubButtonProps> = ({
    customTheme: customThemeProp,
    isDisabled = false,
    isActive = false,
    size = "small",
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    href,
    ...props
}) => {
    const provider = useSidebarContext({
        part: "Sidebar.MenuSubButton"
    });

    const root = useSidebarRootContext({
        part: "Sidebar.MenuSubButton"
    });

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const subItem = useSidebarSubItemContext();

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const height = pointerType === "coarse" ? Math.max(controlHeights[size], touchTargets.preferred) : controlHeights[size];

    const currentColors = getSidebarSubButtonColors({
        isEmphasized: isHovered || isPressed,
        isDisabled,
        isActive,
        colors
    });

    const currentRadius = getSidebarRowRadius({
        variant: root?.variant ?? "sidebar",
        measures,
        radiuses,
        borders
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        item: itemDynamicStyle
    } = useStyles({
        containerColor: currentColors.containerColor,
        isFocusVisible,
        currentRadius,
        shapeConfigs,
        isDisabled,
        iconSizes,
        measures,
        borders,
        height,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        onPress?.();

        if(root?.isPhone) {
            provider?.handleOpenChange(false);
        }
    };

    const isLink = Platform.OS === "web" && !!href && !isDisabled;

    const webProps: SidebarWebPropsType & {
        "aria-current"?: "page";
        href?: string;
    } = Platform.OS === "web" ? {
        "aria-current": isActive ? "page" : undefined,
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet,
        href: isLink ? href : undefined
    } : {};

    const nativeSelectionProps = Platform.OS === "web" ? {} : {
        "aria-selected": props["aria-selected"] ?? (isActive ? true : undefined)
    };

    return <View
        role={subItem ? undefined : "listitem"}
        style={[
            stylesheet.item,
            itemDynamicStyle
        ]}
    >
        <Pressable
            {...props}
            {...interactionHandlers}
            {...webProps}
            {...nativeSelectionProps}
            role={isLink ? "link" : "button"}
            aria-disabled={isDisabled}
            style={[
                containerStyle,
                stylesheet.container,
                containerDynamicStyle
            ]}
            disabled={isDisabled}
            onPress={handlePress}
        >
            <View
                style={[
                    surfaceStyle,
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    isPressed={!isDisabled && isPressed}
                    isHovered={!isDisabled && isHovered}
                    borderRadius={currentRadius}
                    customTheme={customTheme}
                    overlayColor="default"
                    borderWidth={0}
                />
                <Text
                    style={[
                        stylesheet.title,
                        isActive ? {
                            fontWeight: getSidebarActiveWeight({
                                typography
                            })
                        } : undefined
                    ]}
                    customColor={currentColors.titleColor}
                    variant={SIDEBAR_SUB_BUTTON_VARIANT}
                    customTheme={customTheme}
                    numberOfLines={1}
                >
                    {title}
                </Text>
                {subItem?.badge}
            </View>
        </Pressable>
    </View>;
};
export default SidebarMenuSubButton;
